import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CharacteristicSelect from 'Components/characteristicSelect';
import SelectIndicatorsInputToolbox from 'Components/selectIndicatorsInputToolbox';
import ToolboxSelect from 'Components/toolboxSelect';
import util from 'Utils';
import { getQueryDistributions } from 'Selectors/basicData';
import { push } from 'Actions/router';
import toolboxScrollShadow from 'Utils/ui/toolboxScrollShadow';
import * as buttonStyles from '../../styles/buttons.css';
import * as styles from './toolbox.css';

const { mapKV } = util.obj;

class Toolbox extends Component {
  constructor(props) {
    super(props);

    this.state = {
      batch: [],
      ...this.props,
    };

    this.componentDidMount = this.componentDidMount.bind(this);
    this.componentWillUnmount = this.componentWillUnmount.bind(this);

    // Setup table body
    this.tableBodyContainer = document.querySelector('.ag-full-width-container');
    this.appBody = document.querySelector('.theme-cds');
    this.oldHeight = 0;

    // Events
    this.onCharacteristicChange = this.onCharacteristicChange.bind(this);
    this.onDataViewChange = this.onDataViewChange.bind(this);
    this.onYearChange = this.onYearChange.bind(this);
    this.onIndicatorChange = this.onIndicatorChange.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
  }

  componentDidMount() {
    // Save the parent
    this.parent = this.toolbox.parentNode;

    // Append toolbox to table body so it scrolls with table
    this.tableBodyContainer.appendChild(this.toolbox);

    // Used to account for extra spacing at bottom of table when scrollable
    const toolboxHeightAdjust = 20;

    // Get correct offsets to position toolbox
    const box = this.anchor.getBoundingClientRect();
    const tableBodyBox = this.tableBodyContainer.getBoundingClientRect();
    const tableBodyHeight = tableBodyBox.height;
    const toolboxBoxHeight =
      this.toolbox.getBoundingClientRect().height + toolboxHeightAdjust;
    const appHeight = parseInt(this.appBody.style.height.substring(
      0,
      this.appBody.style.height.indexOf('px')
    ), 10);
    let boxOffsetTop = box.top - tableBodyBox.top;
    let boxOffsetLeft = box.left - tableBodyBox.left;
    let boxOffsetBottom = 'auto';

    // Box offset from top of table body + space between arrow and toolbox
    // + height of toolbox
    const totalSpaceNeeded = boxOffsetTop + 30 + toolboxBoxHeight;

    // If box is going to flow off the bottom but we have more room to expand the frame,
    // expand it.
    if (totalSpaceNeeded >= tableBodyHeight &&
      600 >= appHeight &&
      550 >= totalSpaceNeeded) {
      this.oldHeight = appHeight;
      this.appBody.style.height = `${totalSpaceNeeded + 50}px`;
    } else if (totalSpaceNeeded > tableBodyHeight) {
      // If we are close to the bottom of the table, adjust the boxOffsetTop
      boxOffsetTop -= toolboxBoxHeight - 10;
      // If this would go off the top edge of the table, adjust so that
      // it instead sticks to the bottom of the table
      if (0 > boxOffsetTop) {
        boxOffsetTop = 'auto';
        boxOffsetBottom = 0;
      }
      boxOffsetLeft += 30;
    }

    // Inline styles, unfortunately
    this.toolbox.style.top = 'string' === typeof boxOffsetTop ? boxOffsetTop : `${boxOffsetTop + 30}px`;
    this.toolbox.style.bottom = 'string' === typeof boxOffsetBottom ? boxOffsetBottom : `${boxOffsetBottom + 20}px`;
    this.toolbox.style.left = `${boxOffsetLeft - 20}px`;

    // Bind event listener to close toolbox on outside click
    document.addEventListener('click', this.onClickOutside);

    toolboxScrollShadow(this.toolbox);
  }

  componentWillUnmount() {
    // Move back to parent
    this.parent.appendChild(this.toolbox);
    if (this.oldHeight) {
      this.appBody.style.height = `${this.oldHeight}px`;
    }
  }

  onCharacteristicChange(indicator, distribution, toDeselect, hasData = true) {
    const indicatorInBatch =
      this.state.batch.filter((x) => x.indicators === indicator).length;
    const indicators = !hasData ? {
      indicators: indicator,
    } : null;
    this.setState((prevState) => ({
      batch: prevState.batch
        .filter((x) => hasData && x.indicators !== indicator)
        .filter((x) => hasData ||
          (x.indicators !== indicator && Object.keys(x)[0] !== indicator))
        .concat(
          !hasData || (hasData && indicatorInBatch) ?
          toDeselect.reduce((acc, curr) =>
            ([...acc, { [indicator]: { distribution: curr } }]),
          []) :
          {
            [indicator]: { distribution },
          })
        .concat(indicators || [])
        // In cases where we are adding a new indicator which does not have
        // data, we need to sort so that the `indicators` key is on top.
        // this will ensure the routerReducer properly merges the object
        .sort((a, b) => Object.keys(b)[0].localeCompare(Object.keys(a)[0])),
    }));
  }

  onDataViewChange(dataView, fullId) {
    const dynKey = fullId ? 'fullId' : 'id';
    if (this.state.children) {
      this.setState((prevState) => ({
        active: { dataView },
        batch: prevState.batch.concat(
          this.state.children.map(({ [dynKey]: x }) => ({ [x]: { dataView } }))
        ),
      }));
    } else {
      this.setState((prevState) => ({
        active: { dataView },
        batch: prevState.batch.concat({ [prevState[dynKey]]: { dataView } }),
      }));
    }
  }

  onYearChange(year, fullId) {
    const dynKey = fullId ? 'fullId' : 'id';
    if (this.state.children) {
      this.setState((prevState) => ({
        active: { year },
        batch: prevState.batch.concat(
          prevState.children.map(({ [dynKey]: x }) => ({ [x]: { year } }))
        ),
      }));
    } else {
      this.setState((prevState) => ({
        active: { year },
        batch: prevState.batch.concat({ [prevState[dynKey]]: { year } }),
      }));
    }
  }

  onIndicatorChange(indicator, selectAll, hasData) {
    const indicatorDistributions =
      this.state.queryDistributions[indicator] || [];
    const inBatch = this.state.batch.filter(
      (x) => x.indicators === indicator
    ).length;
    if (selectAll && hasData) {
      // If this indicator has data and we are selecting all,
      // Adding all distributions will remove them from the query
      // and display an 'all selected' state
      this.setState((prevState) => ({
        batch: prevState.batch
          .concat(
            indicatorDistributions.reduce((acc, curr) =>
              ([...acc, { [indicator]: { distribution: curr } }]),
            [])
          )
          .concat(inBatch ? { indicators: indicator } : []),
      }));
      // If this indicator is in the batch and we've selected all, we want to
      // revert the change so it is not removed from the table
      if (inBatch) {
        this.setState((prevState) => ({
          batch: prevState.batch.concat({
            indicators: indicator,
          }),
        }));
      }
    } else if (selectAll && !hasData) {
      // If we are selecting all and we have not yet gotten the data for
      // this indicator, filter our all other batch operations and add
      // in an item to add the indicator to the query string
      this.setState((prevState) => ({
        batch: prevState.batch
          .filter((x) => x.indicators !== indicator)
          .filter((x) => !x[indicator])
          .concat({
            indicators: indicator,
          }),
      }));
    } else {
      this.setState((prevState) => ({
        batch: prevState.batch.concat({
          indicators: indicator,
        }),
      }));
    }
  }

  onClickOutside(evt) {
    if ('undefined' === typeof this.toolbox || !this.toolbox) {
      document.removeEventListener('click', this.onClickOutside);
      return;
    }
    if (this.toolbox.contains(evt.target)) {
      return;
    }
    document.removeEventListener('click', this.onClickOutside);
    this.props.onClose();
  }

  render() {
    const {
      onClose,
      onApply,
      years,
      active,
      children,
      dataViews,
      characteristics,
      id,
      fullId,
    } = this.state;

    return (
      <span ref={(ref) => { this.anchor = ref; }}>
        <div className={styles.toolbox} ref={(ref) => { this.toolbox = ref; }}>
          <div className={styles.topBanner}>
            {1 < dataViews.length && (
              <div className={styles.dataView}>
                <span>Data View</span>
                <ToolboxSelect
                  active={active.dataView}
                  fullId={fullId}
                  items={dataViews}
                  onChange={this.onDataViewChange}
                />
              </div>
            )}
            {1 < years.length && (
              <div className={styles.year}>
                <span>Timeframe</span>
                <ToolboxSelect
                  active={active.year}
                  fullId={fullId}
                  items={years.map((value) => ({ value, label: value }))}
                  onChange={this.onYearChange}
                />
              </div>
            )}
            <span className={styles.btnWrapper}>
              <button
                className={[buttonStyles.closeButton, styles.apply].join(' ')}
                onClick={() => { onApply(this.state.batch); onClose(); }}
              >Apply</button>
              <button
                className={[buttonStyles.closeButton, styles.apply].join(' ')}
                onClick={() => { onClose(); }}
              >Close</button>
            </span>
          </div>
          <ul className={styles.indicatorGroup}>
            {children ? (
              <SelectIndicatorsInputToolbox
                id={parseInt(id, 10)}
                key={fullId}
                fullId={fullId}
                catHash={fullId ? fullId.split('|')[1] : null}
                onIndicatorChange={this.onIndicatorChange}
                onCharacteristicChange={this.onCharacteristicChange}
                characteristics={characteristics}
              />
            ) : mapKV(characteristics, (group, key) => 1 <= group.count && (
              <li className={styles.groupChild} key={key}>
                <CharacteristicSelect
                  characteristics={group.values}
                  onChange={this.onCharacteristicChange}
                  indicator={group.indicator}
                  catHash={fullId ? fullId.split('|')[1] : null}
                  hasData
                />
              </li>
            ), true)}
          </ul>
        </div>
      </span>
    );
  }
}

// @TODO update this later
/* eslint-disable react/require-default-props, react/no-unused-prop-types */
Toolbox.propTypes = {
  children: PropTypes.array,
  queryDistributions: PropTypes.object,
  years: PropTypes.array,
  dataViews: PropTypes.array,
  data: PropTypes.array,
  locked: PropTypes.bool,
  characteristics: PropTypes.object.isRequired,
  active: PropTypes.shape({
    dataView: PropTypes.string,
    year: PropTypes.string,
  }),
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
/* eslint-enable */

const mapStateToProps = (state, ownProps) => {
  if (ownProps.children) {
    const refineValues = ownProps.children
      .find((x) => x.years && x.dataViews && x.active);
    refineValues.dataViews = ownProps.children
      .reduce((acc, curr) => ( // Get all data views that any children have
        [
          ...acc,
          ...curr.dataViews,
        ]
      ), [])
      .reduce((acc, curr) => (// Remove duplicates
        (-1 === acc.findIndex((x) => x.label === curr.label) ?
        [
          ...acc,
          curr,
        ] : acc)
      ), []);

    const testValue = ownProps.children
      .find((x) => (
        x.years &&
        x.dataViews &&
        x.active &&
        x.active.dataView !== x.dataViews[0].comparator
      ));

    if ('undefined' !== typeof testValue) {
      refineValues.active = testValue.active;
    }

    return {
      queryDistributions: getQueryDistributions(state),
      years: refineValues.years,
      dataViews: refineValues.dataViews,
      active: refineValues.active,
      characteristics: ownProps.children
        .sort(
          ({ sortId: { id: a } }, { sortId: { id: b } }) => a.localeCompare(b)
        )
        .reduce((
          acc,
          { name, data, locked, id: indicator, characteristicCount: count }
        ) => ({
          ...acc,
          [name]: {
            indicator,
            count,
            values: data.map(({ characteristic, sortId: id }) => ({
              locked,
              characteristic,
              id,
            })),
          },
        }), {}),
    };
  }

  return {
    characteristics: {
      0: {
        indicator: ownProps.id,
        count: ownProps.characteristicCount,
        values: ownProps.data.map(({ characteristic, sortId: id }) => ({
          locked: ownProps.locked,
          characteristic,
          id,
        })),
      },
    },
  };
};

const mapDispatchToProps = (dispatch) => ({
  onApply: (batch) => dispatch(push({ batch })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Toolbox);



// WEBPACK FOOTER //
// ./app/components/toolbox/index.js